/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { createEventEmitter, emitEvent, onEvent, removeAllEventListeners } from "@adapptio/lib-shared";
import { v4 as uuid_v4 } from "uuid";

import { IEditor } from "../../../IEditor";
import { ResourceService } from "../../../resources/ResourceService";
import { RuntimeService } from "../../../runtime/RuntimeService";
import { IView, IViewEditable, IViewInfo } from "../../../view/IView";
import { ViewModel } from "../Common/ViewModel";

export enum VIEW_EDITOR_CEATE_TYPE {
	PAGE = "page",
	WIDGET = "widget"
}

interface IViewEditorCreateParams {
	viewType: VIEW_EDITOR_CEATE_TYPE;
}

export class ViewEditorCreate implements IView<IViewEditorCreateParams>, IViewEditable {
	/* Services */
	private resourceSvc: ResourceService;
	private runtimeSvc: RuntimeService;

	/** View type */
	private viewType: VIEW_EDITOR_CEATE_TYPE;

	/** Generated resource ID */
	private resourceId: string;

	/** View info change event */
	public onViewInfoChanged = createEventEmitter<void>();

	/** Document model */
	private viewModel: ViewModel;

	/** If resource was modified */
	private isModified: boolean;

	/** If the view is being saved */
	private saveInProgress = false;

	/**
	 * View constructor
	 *
	 * @param dc Dependency Container
	 */
	public constructor(dc: IEditor) {
		this.resourceSvc = dc.get("resourceService");
		this.runtimeSvc = dc.get("runtimeService");
	}

	/**
	 * View init
	 *
	 * @param params Params
	 */
	public async init(params: IViewEditorCreateParams): Promise<void> {
		this.viewType = params.viewType;
		this.resourceId = uuid_v4();

		this.viewModel = new ViewModel(this.runtimeSvc, {});
		await this.viewModel.createDefault();

		onEvent(this.viewModel.onModelChange, () => {
			this.setIsModified(true, true);
		});
	}

	/**
	 * Returns view model
	 */
	public getViewModel(): ViewModel {
		return this.viewModel;
	}

	/**
	 * Returns view type
	 */
	public getViewType(): VIEW_EDITOR_CEATE_TYPE {
		return this.viewType;
	}

	/**
	 * Saves a resource
	 * Returns false if create is already in progress or resource was successfully created
	 */
	public async save(): Promise<boolean> {
		if (!this.isModified || this.saveInProgress) {
			return false;
		}

		const content = this.viewModel.exportDocumentAsYAML();

		this.saveInProgress = true;

		// try {
		// 	await this.resourceSvc.saveBlueprint(
		// 		this.resource.id,
		// 		content,
		// 		this.resource.metaData.revision,
		// 		"Editor Save"
		// 	);

		// 	this.setIsModified(this.isModified, true);
		// } finally {
		// 	this.saveInProgress = false;
		// 	emitEvent(this.onResourceInfoChanged);
		// }

		return true;
	}

	/**
	 * Returns if a view is being saved
	 */
	public isBeingSaved(): boolean {
		return this.saveInProgress;
	}

	/**
	 * Disposes view
	 */
	public dispose(): void {
		if (this.viewModel) {
			this.viewModel.dispose();
		}

		removeAllEventListeners(this.onViewInfoChanged);
	}

	private setIsModified(isModified: boolean, notify: boolean) {
		this.isModified = isModified;

		if (notify) {
			emitEvent(this.onViewInfoChanged);
		}
	}

	/**
	 * Returns if the view has been modified
	 */
	public getIsModified(): boolean {
		return this.isModified;
	}

	/**
	 * Returns view info
	 */
	public getViewInfo(): IViewInfo {
		return {
			// @todo translate
			label: this.viewType === VIEW_EDITOR_CEATE_TYPE.PAGE ? "New Page" : "New Widget",
			icon: "mdi/view-dashboard",
			canClose: true,
			isModified: this.isModified,
			renderWhenNotActive: true
		};
	}
}
